import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { db, ScreenplayEntity } from '@/shared/lib/db'
import { Character } from '@storyverse/shared'
import { $getRoot } from 'lexical'
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { SuggestionListItem } from '../type'
import { NodeType } from './nodes/NodeType'
import { SceneNode } from './nodes/SceneNode'

import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useScreenplayEditor } from '../../hooks/useScreenplayEditor'
import { useSpEditorStore } from '../../stores/sp-editor.store'
import { PageBreakNode } from './nodes/PageBreakNode'
import { TSuggestionProps } from './plugins/SpSuggestionPlugin'
import { getCurrentNode } from './utils/NodeUtils'

// Context is no longer needed for state, but might be used for Editor instance if not in store?
// Store has editor instance. 
// So we can technically remove the context entirely and have components use the store hook.
// However, to avoid breaking all child components immediately (if they use useSpEditorProvider), I should likely keep the hook `useSpEditorProvider` but duplicate the logic to return store values, or explicitly tell the user I am breaking it. 
// The plan said: "Components relying on useSpEditorProvider will need to be updated."
// So I will remove the context and update consumers? 
// Or I can map the store state to the context value to maintain backward compatibility during the refactor?
// Mapping is safer. 

type SpEditorContextType = {
    characters: Character[]
    addCharacter: (character: Character) => void
    featuresEnabled: FeatureEnabled
    updateFeatureEnabled: (feature: FeatureEnabled) => void
    suggestionProps: TSuggestionProps<SuggestionListItem>
    setSuggestionProps: Dispatch<SetStateAction<TSuggestionProps<SuggestionListItem>>>
    htmlString: string
    setHtmlString: (htmlString: string) => void
    screenplayEntity: ScreenplayEntity | undefined
    setScreenplayEntity: Dispatch<SetStateAction<ScreenplayEntity | undefined>>
    project: any
}
type FeatureEnabled = {
    aiCompletion: boolean
    nodeToolbar: boolean
    suggestionDropdown: boolean
}

// Keeping the context for now to minimize refactor blast radius, but backing it with the store
export const SpEditorContext = createContext<SpEditorContextType | null>(null)

export function SpEditorProvider({ children }: { children: React.ReactNode }) {
    const { project } = useWorkspace()
    const store = useSpEditorStore()
    const { setScenes } = useSceneStore()
    const { characters, addCharacter } = useCharactersStore()

    // Initialize editor logic (loads characters, features)
    useScreenplayEditor()

    const { editor } = useSceneStore()

    // Mutation listeners
    useEffect(() => {
        if (!editor) return

        return editor.registerMutationListener(
            SceneNode,
            (mutatedNodes, { updateTags, dirtyLeaves, prevEditorState }) => {
                const hasCreatedOrDestroyed = () => {
                    for (const val of mutatedNodes.values()) {
                        if (val === 'created' || val === 'destroyed') return true
                    }
                    return false
                }
                editor?.getEditorState().read(() => {

                    const sceneNodes = $getRoot().getChildren().filter((e) => {
                        return e instanceof SceneNode
                    })
                    setScenes(sceneNodes)

                })
                if (hasCreatedOrDestroyed()) {
                    editor.update(() => {
                        const currentNode = getCurrentNode()
                        currentNode?.getNextSiblings()?.forEach((node) => {
                            if (node.__type === NodeType.Scene) {
                                ; (node as SceneNode).markDirty()
                            }
                        })
                    })
                }
            }
        )
    }, [editor])

    useEffect(() => {
        if (!editor) return

        return editor.registerMutationListener(
            PageBreakNode,
            (mutatedNodes, { updateTags, dirtyLeaves, prevEditorState }) => {
                const hasCreatedOrDestroyed = () => {
                    for (const val of mutatedNodes.values()) {
                        if (val === 'created' || val === 'destroyed') return true
                    }
                    return false
                }

                if (hasCreatedOrDestroyed()) {
                    editor.update(() => {
                        const currentNode = getCurrentNode()
                        currentNode?.getNextSiblings()?.forEach((node) => {
                            if (node.__type === NodeType.PageBreak) {
                                ; (node as PageBreakNode).markDirty()
                            }
                        })
                    })
                }
            }
        )
    }, [editor])

    // Compatibility layer: Map store functions to context shape
    const contextValue: SpEditorContextType = {
        characters: characters,
        addCharacter: async (c) => {
            if (!project?.id) return
            const fullCharacter = {
                ...c,
                id: c.id ?? crypto.randomUUID(),
                projectId: project.id,
            }
            await db.characters.add(fullCharacter)
            addCharacter(fullCharacter)
        },
        featuresEnabled: store.featuresEnabled,
        updateFeatureEnabled: (f) => store.updateFeatureEnabled(f),
        suggestionProps: store.suggestionProps,
        // @ts-ignore - setState compatibility
        setSuggestionProps: (val) => {
            if (typeof val === 'function') {
                // mimic setState function update if needed, or just warn. 
                // store.setSuggestionProps(val(store.suggestionProps)) 
                // simpler to just expect value for now or fix call sites
                const newVal = val(useSpEditorStore.getState().suggestionProps)
                store.setSuggestionProps(newVal)
            } else {
                store.setSuggestionProps(val)
            }
        },
        htmlString: store.htmlString,
        setHtmlString: (s) => store.setHtmlString(s),
        screenplayEntity: store.screenplayEntity,
        // @ts-ignore
        setScreenplayEntity: (val) => {
            if (typeof val === 'function') {
                const newVal = val(useSpEditorStore.getState().screenplayEntity)
                store.setScreenplayEntity(newVal)
            } else {
                store.setScreenplayEntity(val)
            }
        },
        project: project,
    }

    return (
        <SpEditorContext.Provider value={contextValue}>
            {children}
        </SpEditorContext.Provider>
    )
}

export function useSpEditorProvider() {
    const context = useContext(SpEditorContext)
    if (!context)
        throw new Error('useSpEditorProvider must be used within a ScreenplayEditorProvider')
    return context
}
